import {
  useCallback,
  useEffect,
  useRef,
  type Dispatch,
  type SetStateAction,
} from "react";
import type { NavigateFunction } from "react-router-dom";
import handleDeleteMoodForSelectedDate from "utils/moods/handleDeleteMoodForSelectedDate";
import checkInService from "../../../services/CheckInService";
import type { ExistingRecord } from "../checkin-context";
import { Cause } from "../../../types/models";

export type CheckInCauseLike = {
  id?: string;
  causeValue?: string;
  causeTypeId?: string;
  [key: string]: unknown;
};

type CauseLike = CheckInCauseLike;

type SaveCheckInResult = {
  ok: boolean;
  mode?: string;
  entry?: unknown;
};

export type UseCheckInViewHandlersParams = {
  userId: string | null | undefined;
  organizationId: string | null | undefined;
  teamId: string | null | undefined;
  feelingName: string | null | undefined;
  selectedDate: string | Date | null | undefined;
  causes: unknown[] | null | undefined;
  selectedCauses: CauseLike[];
  duration: number | null;
  selectedDuration: number;
  existingRecord: ExistingRecord | null | undefined;
  setMoods: Dispatch<SetStateAction<unknown[] | null | undefined>>;
  clearCache?: ((key: string) => void) | null | undefined;
  refreshTeamMoods?: (() => void) | null;
  onClose?: (() => void) | null;
  clearCauses: () => void;
  selectCause: (cause: CauseLike) => void;
  setCausesToOffer: (causes: CauseLike[]) => void;
  causesToOffer: CauseLike[];
  setSavedCauses: Dispatch<SetStateAction<Cause[]>>;
  activeCauseTypes: { id: string }[];
  causeTypes: { id: string; [key: string]: unknown }[];
  step: string;
  moods: unknown[] | null | undefined;
  selectingMood: number | null | undefined;
  feelingNames: string[];
  customDate: string;
  setRelevantMoods: Dispatch<SetStateAction<Record<string, unknown>>>;
  setSelectingMood: Dispatch<SetStateAction<number | null | undefined>>;
  setDisplayedText: (v: string) => void;
  navigate: NavigateFunction;
  setCauses: Dispatch<SetStateAction<unknown[]>>;
  setStep: (s: string) => void;
  setRewardIdx: (n: number | null) => void;
  rewardsLength: number;
};

export function useCheckInViewHandlers({
  userId,
  organizationId,
  teamId,
  feelingName,
  selectedDate,
  causes,
  selectedCauses,
  duration,
  selectedDuration,
  existingRecord,
  setMoods,
  clearCache,
  refreshTeamMoods,
  onClose,
  clearCauses,
  selectCause,
  setCausesToOffer,
  causesToOffer,
  setSavedCauses,
  activeCauseTypes,
  causeTypes,
  step,
  moods,
  selectingMood,
  feelingNames,
  customDate,
  setRelevantMoods,
  setSelectingMood,
  setDisplayedText,
  navigate,
  setCauses,
  setStep,
  setRewardIdx,
  rewardsLength,
}: UseCheckInViewHandlersParams) {
  const hasRunRewardRef = useRef(false);

  const saveCauseToFirestore = useCallback(
    async (
      causeValue: string,
      moodId: string,
      _causeTypeId: unknown,
      timestamp: string | Date,
      negative: unknown = null
    ) => {
      if (
        causeValue === "skip" ||
        (Array.isArray(causes) && (causes as string[]).includes(causeValue))
      )
        return;
      try {
        const { CauseService } = await import("../../../services/CauseService");

        const result = await CauseService.save({
          causeTypeId: causeValue,
          moodId,
          timestamp: timestamp as Date,
          negative: negative as null,
          feelingName: feelingName as string,
          causes: (causes as []) ?? [],
          userId: userId as string,
        });

        /*
        if (!result.success) {
          throw new Error(result.error || "Failed to save cause");
        }

         */

        return {
          id: result?.id,
          mood: feelingName,
          uid: userId,
          cause: causeValue,
          moodId: moodId,
          timestamp,
          negative,
          causeTypeId: causeValue,
        };
      } catch (error) {
        console.error("Error saving cause to Firestore:", error);
      }
    },
    [causes, feelingName, userId]
  );

  const handleProceed = useCallback(
    async (
      skip = false,
      overrideCauses?: CauseLike[],
      overrideDuration: number | null = null
    ) => {
      try {
        const durationToUse = overrideDuration ?? duration ?? selectedDuration;
        const result = (await checkInService(
          overrideCauses,
          selectedCauses,
          overrideDuration,
          durationToUse,
          existingRecord,
          selectedDate,
          undefined,
          undefined,
          feelingName,
          causes,
          skip,
          userId ?? undefined,
          organizationId ?? undefined,
          teamId ?? undefined
        )) as SaveCheckInResult | undefined;
        if (result?.ok) {
          if (result.mode === "new" && result.entry) {
            setMoods((prev) => [...(prev || []), result.entry]);
          }
          if (clearCache && typeof clearCache === "function") {
            clearCache("moods");
            if (selectedCauses?.length > 0) {
              clearCache("causes");
            }
          }
          refreshTeamMoods?.();
        }

        if (onClose) {
          onClose();
        }
      } catch (error) {
        console.error("CheckIn save failed:", error);
      }
    },
    [
      causes,
      clearCache,
      duration,
      feelingName,
      existingRecord,
      onClose,
      refreshTeamMoods,
      selectedCauses,
      selectedDate,
      selectedDuration,
      setMoods,
      userId,
    ]
  );

  const handleCauseSelect = useCallback(
    async (causeValue: CauseLike) => {
      if (causeValue.causeValue === "skip") {
        clearCauses();
        await handleProceed(true, []);
        return;
      }
      selectCause(causeValue);
    },
    [clearCauses, handleProceed, selectCause]
  );

  useEffect(() => {
    if (step !== "reward" || hasRunRewardRef.current) return;

    hasRunRewardRef.current = true;

    void handleProceed().then(() => {
      setTimeout(() => {
        onClose?.();
      }, 5000);
    });
  }, [step, handleProceed, onClose]);

  const handleCauseDelete = useCallback(
    async (cause: CauseLike) => {
      try {
        const { CauseService } = await import("../../../services/CauseService");

        const result = await CauseService.deleteCause(cause.id as string);

        if (!result.success) {
          throw new Error(result.error || "Failed to delete cause");
        }

        setSavedCauses((prev) => {
          const list = prev ?? [];
          const removedItem = list.find((m) => m.id === cause.id) ?? null;
          const updatedMatching = list.filter((m) => m.id !== cause.id);

          const activeCauseTypeIds: string[] = [];
          activeCauseTypes.forEach((el) => {
            activeCauseTypeIds.push(el.id);
          });

          if (
            removedItem &&
            removedItem.causeTypeId &&
            activeCauseTypeIds.includes(removedItem.causeTypeId)
          ) {
            const deletedCause = causeTypes.find(
              (causeType) => causeType.id == cause.causeTypeId
            );

            if (deletedCause) {
              setCausesToOffer([...causesToOffer, deletedCause as CauseLike]);
            }
          }

          return updatedMatching;
        });
      } catch (error) {
        console.error("Failed to delete cause:", error);
      }
    },
    [
      activeCauseTypes,
      causeTypes,
      causesToOffer,
      setSavedCauses,
      setCausesToOffer,
      userId,
    ]
  );

  const onCauseFlowFinish = useCallback(() => {
    console.log("clicked 2");
    setRewardIdx(Math.floor(Math.random() * rewardsLength));
  }, [rewardsLength, setRewardIdx, setStep]);

  const onDeleteMoodFromCauseSelector = useCallback(() => {
    handleDeleteMoodForSelectedDate(
      causes,
      selectingMood,
      feelingNames,
      selectedDate,
      customDate,
      moods,
      userId,
      setMoods,
      setRelevantMoods,
      setSelectingMood,
      setDisplayedText,
      navigate,
      setCauses
    );
  }, [
    causes,
    customDate,
    feelingNames,
    moods,
    navigate,
    selectedDate,
    selectingMood,
    setCauses,
    setDisplayedText,
    setMoods,
    setRelevantMoods,
    setSelectingMood,
    userId,
  ]);

  const onDeleteMoodFooter = useCallback(() => {
    try {
      handleDeleteMoodForSelectedDate(
        causes,
        selectingMood,
        feelingNames,
        selectedDate,
        customDate,
        moods,
        userId,
        setMoods,
        setRelevantMoods,
        setSelectingMood,
        setDisplayedText,
        navigate,
        setCauses
      );

      onClose?.();
    } catch {
      /* same as original */
    }
  }, [
    causes,
    customDate,
    feelingNames,
    moods,
    navigate,
    onClose,
    selectedDate,
    selectingMood,
    setCauses,
    setDisplayedText,
    setMoods,
    setRelevantMoods,
    setSelectingMood,
    userId,
  ]);

  return {
    saveCauseToFirestore,
    handleProceed,
    handleCauseSelect,
    handleCauseDelete,
    onCauseFlowFinish,
    onDeleteMoodFromCauseSelector,
    onDeleteMoodFooter,
  };
}
